import React from 'react';
import PropTypes from 'prop-types';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import getBreadcrumbs from 'components/CreatorStoreFront/helpers/getBreadCrumbs';
import ProfileDisplay from 'components/CreatorStoreFront/ProfileDisplay';
import ProfileDisplayMiniHeader from 'components/CreatorStoreFront/ProfileDisplayMiniHeader';
import SimpleChicletNav from 'components/CreatorStoreFront/SimpleChicletNav';
import Breadcrumb from 'components/Content/Breadcrumb';
import { csfRouteComponents, csfDynamicRoutes } from 'components/CreatorStoreFront/views/CSFViews';
import { getCsfRoute } from 'components/CreatorStoreFront/helpers/getCsfRoute';
import getPageTypeFromPath from 'components/CreatorStoreFront/helpers/getPageTypeFromPath';
import { navigateTo } from 'components/CreatorStoreFront/helpers/csfNavigation';
import { CSF_PAGE_TYPES } from 'constants/actionTypes/creatorStoreFront';
import { Container } from 'components/ui';
import ConstructorCarousel from 'components/ConstructorCarousel';
import RecServiceCarousel from 'components/RecServiceCarousel';
import { CONSTRUCTOR_PODS, GROUPING } from 'constants/constructorConstants';
import { CSF_DC_ATB_SOURCES } from 'constants/CsfDynamicCommissioning';
import CSFBackToTopButton from 'components/CreatorStoreFront/CSFBackToTopButton';
import { initializeInstagramEmbeds } from 'components/CreatorStoreFront/helpers/socialMediaEmbed';
import Empty from 'constants/empty';
import {
    captureAttributionData,
    captureCSFAdobeTrackingCode,
    updateAttributionNavigation
} from 'components/CreatorStoreFront/helpers/csfAttribution';
import {
    captureCsfDynamicCommissioningU5FromProfile,
    cleanExpiredJourneys,
    clearActiveCommissioningU1State,
    clearCsfDynamicCommissioningU5,
    getActiveCommissioningU1State,
    getContentTabContextAfterProfileLoad,
    initializeActiveCommissioningU1State,
    getCsfDynamicCommissioningU5,
    getNavigationTypeFromPopState,
    initCsfNavIndex,
    isDynamicCommissioningEnabled,
    isRealBrowserHistoryNavigation,
    logCsfEntryInComponent,
    logCsfPageViewInComponent
} from 'utils/CreatorStoreFront/dynamicCommissioning';
import JavascriptUtils from 'utils/javascript';
import ProfileError from 'components/CreatorStoreFront/CSFErrors/ProfileError';
import SectionContentError from 'components/CreatorStoreFront/CSFErrors/SectionContentError';
import CSFContentEmpty from 'components/CreatorStoreFront/CSFContentEmpty/CSFContentEmpty';
import SpaUtils from 'utils/Spa';
import localeUtils from 'utils/LanguageLocale';
import CreatorStoreFrontEN from 'components/CreatorStoreFront/locales/CreatorStoreFront_en_US';
import CreatorStoreFrontFR from 'components/CreatorStoreFront/locales/CreatorStoreFront_fr_CA';

const { isObjectEmpty } = JavascriptUtils;
const { getLocalisedDataFromResource } = localeUtils;
const getCsfText = getLocalisedDataFromResource(CreatorStoreFrontEN, CreatorStoreFrontFR);

class CreatorStoreFront extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            currentComponent: null,
            componentProps: Empty.Object,
            handle: '',
            isReady: false,
            section: '',
            isError: false
        };
        // Monotonically incremented per `maybeRefreshOnCreatorChange` invocation
        // so overlapping SPA creator switches (A→B→C where B's Profile API
        // response lands after C's) can detect that they were superseded and
        // skip writes to `CSF_DYNAMIC_COMMISSIONING_DATA` / `state.handle` that would otherwise
        // overwrite the newer creator's state with stale data.
        this.creatorRefreshRequestId = 0;
    }

    async componentDidMount() {
        const { dispatch, initializeCSFCreatorProfileData } = this.props;
        const route = getCsfRoute(window.location.pathname);
        const { handle, identifier } = route;
        const section = route.section || CSF_PAGE_TYPES.FEATURED;

        this.setState({ handle, section });

        try {
            const profilePayload = await initializeCSFCreatorProfileData(handle);

            this.updateCsfSeoMetadata(profilePayload?.creatorProfile?.displayName);

            // Capture attribution data once on initial load with identifier (uses fresh profile when available)
            this.captureCSFAttributionData(section, identifier, profilePayload?.creatorProfile);

            // Dynamic-commissioning u5 capture is fully decoupled from legacy
            // attribution: it reads only the fresh Profile API response and
            // writes only to the standalone CSF_DYNAMIC_COMMISSIONING_DATA record.
            captureCsfDynamicCommissioningU5FromProfile(profilePayload?.creatorProfile);

            if (isDynamicCommissioningEnabled()) {
                // Seed monotonic history-state index for back/forward detection.
                initCsfNavIndex();

                // CSF-1699: skip the journey reset + entry log AND the TTL
                // cleanup when the Profile API refresh failed.
                // `initializeCSFCreatorProfileData` swallows errors and
                // returns undefined (see `actions/CreatorStoreFrontActions.js`);
                // a missing `creatorProfile` is the only signal we get. Per
                // Eduardo's dev notes: "If the new creator/profile load fails,
                // do not clear the existing IndexedDB journey." This applies
                // to `cleanExpiredJourneys()` too — it removes expired
                // journeys from the same storage and would otherwise mutate
                // it on the failure path. TTL cleanup is best-effort
                // housekeeping; deferring it to the next successful entry is
                // safe (the journey container is still TTL-bounded).
                if (profilePayload?.creatorProfile) {
                    // CSF-1773: entry initializes persisted active/current U1
                    // in lockstep with the IndexedDB entry event.
                    await logCsfEntryInComponent({
                        destinationUrl: window.location.href,
                        creatorProfile: profilePayload.creatorProfile,
                        section,
                        identifier,
                        u5: getCsfDynamicCommissioningU5()
                    });
                    await cleanExpiredJourneys();
                }

                // CSF-1791: no deferred Rakuten ping here — `logCsfEntryInComponent`
                // now fetches the event `siteId` inline (the `/rakuten/site-id`
                // call IS the Rakuten attribution ping) and persists the
                // `{ u1, siteId }` pair before returning. Page views reuse
                // the persisted pair, so a separate refresh would be a
                // redundant refetch and violates the AC ("page_view should
                // not refetch").
            }

            const { contentSummary, formattedMenuItemsForRedirect } = getContentTabContextAfterProfileLoad({
                profilePayload,
                props: this.props,
                pathname: window.location.pathname
            });

            // Redirect to the next active tab with available content when a user hits a route without content
            // Redirection is based on hierarchy featured --> products --> collections --> posts
            // If all routes are missing content, the system defaults to ContentEmpty state hanled at HOC
            const isFeaturedContentMissing = section === CSF_PAGE_TYPES.FEATURED && contentSummary?.featuredCount === 0;
            const isProductsContentMissing = section === CSF_PAGE_TYPES.PRODUCTS && contentSummary?.productsCount === 0;
            const isCollectionsContentMissing = section === CSF_PAGE_TYPES.COLLECTIONS && contentSummary?.collectionsCount === 0;
            const isPostsContentMissing = section === CSF_PAGE_TYPES.POSTS && contentSummary?.postsCount === 0;

            const isMissingContent = isFeaturedContentMissing || isProductsContentMissing || isCollectionsContentMissing || isPostsContentMissing;
            const nextAvailableRoute = formattedMenuItemsForRedirect[0];

            if (isMissingContent && nextAvailableRoute) {
                const basePath = `/creators/${handle}`;
                const link = nextAvailableRoute?.link;
                const redirectURL = link === '/' ? basePath : `${basePath}/${String(link).replace(/^\//, '')}`;

                await navigateTo(redirectURL, dispatch, true, false);
                this.setState({ isReady: true });

                return;
            }

            await this.fetchPageDataForPath(window.location.pathname);
            this.renderRouteFromPath(window.location.pathname);
            this.setState({ isReady: true });

            window.addEventListener('popstate', this.handleRouting);
            window.addEventListener('csfNavigationComplete', this.handleNavigationComplete);
        } catch (error) {
            Sephora.logger.error('Error initializing CreatorStoreFront:', error);
            this.setState({ isError: true });
        }

        // Initialize Instagram embed early to avoid race conditions in post detail component.
        initializeInstagramEmbeds();
    }

    componentDidUpdate(prevProps, prevState) {
        const { creatorStoreFrontData, pageData } = this.props;
        const { pageType: currentPageType } = creatorStoreFrontData || Empty.Object;
        const { pageType: prevPageType } = prevProps.creatorStoreFrontData || Empty.Object;

        const pageTypeChanged = currentPageType !== prevPageType;
        const pageDataChanged = pageData !== prevProps.pageData;

        const route = getCsfRoute(window.location.pathname);
        const section = route.section || CSF_PAGE_TYPES.FEATURED;

        if (section?.length && prevState?.section !== section) {
            this.setState({ section });
        }

        if (pageTypeChanged || pageDataChanged) {
            this.renderRouteFromPath(window.location.pathname);
        }

        const prevDisplayName = prevProps.creatorProfileData?.creatorProfile?.displayName;
        const currentDisplayName = this.props.creatorProfileData?.creatorProfile?.displayName;

        if (prevDisplayName !== currentDisplayName) {
            this.updateCsfSeoMetadata();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.handleRouting);
        window.removeEventListener('csfNavigationComplete', this.handleNavigationComplete);
    }

    fetchPageDataForPath = async path => {
        const pageType = getPageTypeFromPath(path) || CSF_PAGE_TYPES.FEATURED;
        await this.props.initializeCSFPageData?.(pageType, path);
    };

    /**
     * Handles SPA navigation between creator storefronts (e.g. /creators/A → /creators/B
     * without a full reload). Refreshes ONLY the Dynamic Commissioning state — re-fetches
     * the new creator's Profile API response, captures the new u5 directly into the
     * standalone CSF_DYNAMIC_COMMISSIONING_DATA record (or clears it when the Profile API does not return one),
     * and logs a fresh entry event for the new journey. Legacy `csfAttributionData` is
     * not touched here — its SPA-navigation refresh is owned by `updateAttributionNavigation`,
     * called separately in the navigation handlers. No-op when the handle did not change.
     */
    maybeRefreshOnCreatorChange = async (newHandle, section, identifier) => {
        // Explicit result shape so callers can tell apart:
        //  - "no change happened" (continue normally with current props),
        //  - "change attempted but profile refresh failed" (must skip DC
        //    page_view logging — falling back to stale `creatorProfileData`
        //    would attribute the new URL to the previous creator), and
        //  - "this call was superseded by a newer creator switch" (caller
        //    must bail entirely so the newer call owns `updateAttributionNavigation`
        //    and `logCsfPageView` for the latest URL — otherwise we'd
        //    double-log or attribute the new URL to a stale creator).
        if (!newHandle || newHandle === this.state.handle) {
            return { profile: null, refreshFailed: false, superseded: false };
        }

        // Capture the call's sequence id up front; `isStale()` returns true
        // as soon as a later invocation has incremented the counter. Used to
        // bail before any write to `CSF_DYNAMIC_COMMISSIONING_DATA` or `state.handle` so a slower
        // earlier Profile API response cannot overwrite the newer creator's
        // state in an A→B→C rapid-switch scenario.
        const requestId = ++this.creatorRefreshRequestId;
        const isStale = () => requestId !== this.creatorRefreshRequestId;

        try {
            const profilePayload = await this.props.initializeCSFCreatorProfileData?.(newHandle);

            // A newer creator switch started while we awaited the Profile API.
            // Bail without touching `CSF_DYNAMIC_COMMISSIONING_DATA` / `handle`; the newer call
            // owns those writes (and its own staleness checks).
            if (isStale()) {
                return { profile: null, refreshFailed: false, superseded: true };
            }

            const freshProfile = profilePayload?.creatorProfile;

            // `initializeCSFCreatorProfileData` swallows errors internally —
            // it dispatches `{ error: true }` and returns `undefined` rather
            // than throwing (see `actions/CreatorStoreFrontActions.js`). So a
            // missing payload (or missing `creatorProfile`) is the only
            // signal we get for a failed Profile API refresh; the outer
            // `try/catch` here will not fire on its own.
            //
            // Treat a missing `freshProfile` as failure:
            //  - clear the persisted DC `u5` / active U1 so stale values from
            //    the previous creator cannot leak into the new creator's pings,
            //  - bail without committing `handle` so the next navigation to
            //    this creator retries instead of being short-circuited by
            //    the early-return guard above,
            //  - signal `refreshFailed: true` so the caller skips the
            //    follow-up `logCsfPageView` (otherwise it would fall back
            //    to the stale `creatorProfileData` prop and log the new
            //    URL with the previous creator's attribution).
            if (!freshProfile) {
                clearCsfDynamicCommissioningU5();
                clearActiveCommissioningU1State();

                return { profile: null, refreshFailed: true, superseded: false };
            }

            // Dynamic-commissioning capture is fully decoupled from legacy attribution.
            captureCsfDynamicCommissioningU5FromProfile(freshProfile);

            if (isDynamicCommissioningEnabled()) {
                // CSF-1699: do NOT log a fresh `entry` event when the user
                // switches creators inside the same browsing session
                // (browser back/forward across creators, or any future
                // SPA cross-creator path). Per Eduardo's dev notes:
                // "When back/forward navigation moves between different
                // creator pages: do not clear the existing IndexedDB
                // journey; do not log a new `entry` event."
                //
                // CSF-1773/1791: refresh active/current U1 for the new creator
                // so in-journey page_view / ATB read the correct U1. The
                // initializer now also fetches the matching event `siteId`
                // (the `/rakuten/site-id` call IS the Rakuten attribution
                // ping) and persists `{ u1, siteId }` together — must
                // `await` so the immediately-following `page_view` (logged
                // by the caller) reads the populated pair instead of racing
                // the in-flight fetch.
                //
                // CSF-1791 rapid-switch defense (A→B→C): the initializer
                // awaits `/rakuten/site-id` before persisting. Pass our
                // `isStale` token so a slower earlier call cannot overwrite
                // a newer creator's persisted `{ u1, siteId }` pair after
                // the post-await `isStale()` check below has tripped.
                await initializeActiveCommissioningU1State({
                    creatorProfile: freshProfile,
                    section,
                    identifier,
                    isStillCurrent: () => !isStale()
                });

                // A newer switch may have landed while the Profile API
                // call (or the siteId fetch inside the initializer) was in
                // flight. Skip the `handle` commit so the newer call owns
                // the final committed state.
                if (isStale()) {
                    return { profile: null, refreshFailed: false, superseded: true };
                }
            }

            // Commit the new handle only after the profile fetch (and DC
            // capture) succeeded — if the fetch failed, leaving `handle`
            // unchanged lets the next navigation to this creator retry
            // instead of being short-circuited by the early-return guard.
            this.setState({ handle: newHandle });

            // Return the freshly loaded profile so callers can thread it into
            // the immediately-following page_view log without waiting for
            // React props to update (props from the dispatch above are not
            // yet visible in the same tick).
            return { profile: freshProfile, refreshFailed: false, superseded: false };
        } catch (err) {
            // Profile fetch failed → DC capture never ran, so any persisted
            // u5 / active U1 still belongs to the previous creator. Drop both
            // to prevent cross-creator leakage into subsequent pings/page_views.
            // Cleared via the DC-module accessor so this stays inside the
            // DC namespace (no legacy-storage coupling).
            //
            // Skip the clear when this call was already superseded — a
            // newer in-flight switch may have captured the correct u5 for
            // the new creator and we'd erase valid data.
            if (!isStale()) {
                clearCsfDynamicCommissioningU5();
                clearActiveCommissioningU1State();
            }

            Sephora.logger.error('Error refreshing CSF state on creator change:', err);

            return { profile: null, refreshFailed: !isStale(), superseded: isStale() };
        }
    };

    handleRouting = async event => {
        // Top-level guard: this is an async DOM event listener, so any
        // uncaught throw becomes an `unhandledrejection` rather than a
        // normal handler exception. Wrap the whole body so failures stay
        // logged and visible instead of leaking to the browser.
        try {
            const path = window.location.pathname;
            const { handle, section, identifier } = getCsfRoute(path);
            const navigationType = getNavigationTypeFromPopState(event);
            const isBrowserHistoryNavigation = isRealBrowserHistoryNavigation(event);

            const { profile: refreshedProfile, refreshFailed, superseded } = await this.maybeRefreshOnCreatorChange(handle, section, identifier);

            // A newer creator switch took ownership of this navigation's DC
            // state mid-flight. Bail entirely so the newer handler owns
            // `updateAttributionNavigation` and `logCsfPageView` for the
            // current URL — running them here would either double-log or
            // attribute the latest URL to a stale creator.
            if (superseded) {
                return;
            }

            // CSF-1793: when DC KS is on, DC owns CSF attribution end-to-end
            // — skip the legacy navigation refresh. DC's own SPA/back-forward
            // logging (below) continues to run.
            if (isBrowserHistoryNavigation && !isDynamicCommissioningEnabled()) {
                updateAttributionNavigation(handle, section, identifier);
            }

            // Real browser back/forward → log a page_view. When direction
            // cannot be derived from history state, `navigationType` may be
            // null and should still be forwarded. Synthetic popstate from
            // `navigateTo({ dispatchPopState: true })` is handled via
            // `csfNavigationComplete` instead.
            //
            // Skip when the creator changed but the Profile API refresh
            // failed: `creatorProfileOverride` would be null and
            // `logCsfPageView` would fall back to `creatorProfileData`
            // (still the previous creator), attributing the new URL to
            // the wrong creator.
            //
            // CSF-1699: also skip when the creator changed successfully
            // (refreshedProfile is non-null). Per Eduardo's dev notes,
            // back/forward across creators must "not log a `page_view`
            // event for the cross-creator navigation". Same-creator
            // back/forward (refreshedProfile null because handle did not
            // change) still logs a page_view as before.
            const isCrossCreatorNavigation = Boolean(refreshedProfile);

            if (isDynamicCommissioningEnabled() && isBrowserHistoryNavigation && !refreshFailed && !isCrossCreatorNavigation) {
                this.logCsfPageView({ navigationType, creatorProfileOverride: refreshedProfile });
            }

            try {
                await this.fetchPageDataForPath(path);
            } catch (err) {
                Sephora.logger.error('Error fetching page data on route change:', err);
            }

            this.renderRouteFromPath(path);
        } catch (err) {
            Sephora.logger.error('Error handling CSF route change:', err);
        }
    };

    handleNavigationComplete = async event => {
        // Top-level guard: this is an async DOM event listener, so any
        // uncaught throw becomes an `unhandledrejection` rather than a
        // normal handler exception. Wrap the whole body so failures stay
        // logged and visible instead of leaking to the browser.
        try {
            const path = event.detail.path;
            const { handle, section, identifier } = getCsfRoute(path);

            const { profile: refreshedProfile, refreshFailed, superseded } = await this.maybeRefreshOnCreatorChange(handle, section, identifier);

            // See `handleRouting` for the rationale: the newer in-flight
            // creator switch owns attribution + page_view for the latest URL.
            if (superseded) {
                return;
            }

            // Update attribution on navigation (preserves existing U1).
            // CSF-1793: when DC KS is on, DC owns CSF attribution — skip
            // the legacy SPA-navigation refresh. DC's page_view (below)
            // continues to run.
            if (!isDynamicCommissioningEnabled()) {
                updateAttributionNavigation(handle, section, identifier);
            }

            // SPA navigation inside CSF (incl. tab clicks) → page_view with no
            // navigation_type and source_url=null per CSF-1686 AC.
            //
            // Skip on a failed creator refresh — see the equivalent guard in
            // `handleRouting` for the rationale (avoid logging the new URL
            // with stale previous-creator attribution from `creatorProfileData`).
            if (isDynamicCommissioningEnabled() && !refreshFailed) {
                this.logCsfPageView({ navigationType: null, creatorProfileOverride: refreshedProfile });
            }

            this.renderRouteFromPath(path);
        } catch (err) {
            Sephora.logger.error('Error handling CSF SPA navigation complete:', err);
        }
    };

    logCsfPageView = ({ navigationType, creatorProfileOverride = null } = {}) => {
        // CSF-1773: reuse persisted active/current U1 for in-journey navigation.
        const creatorProfile = creatorProfileOverride ?? this.props.creatorProfileData?.creatorProfile;
        const expectedSubAffiliateId = creatorProfile?.attributionDetails?.subAffiliateId ?? null;
        const active = getActiveCommissioningU1State({ expectedSubAffiliateId });

        logCsfPageViewInComponent({
            destinationUrl: window.location.href,
            creatorId: creatorProfile?.creatorId ?? null,
            u1: active?.u1 ?? null,
            siteId: active?.siteId ?? null,
            u5: getCsfDynamicCommissioningU5({ expectedSubAffiliateId }),
            navigationType,
            sourceUrl: null
        });
    };

    captureCSFAttributionData = (section, identifier, creatorProfileOverride) => {
        const creatorProfile = creatorProfileOverride ?? this.props.creatorProfileData?.creatorProfile;

        // CSF-1793: Mutually-exclusive caller-side branching by DC KS.
        // KS on  — DC owns CSF attribution; legacy is fully gated off. Adobe
        //          Analytics (eVar85) is preserved via a dedicated Adobe-only
        //          `om_mmc` capture into `CSF_ADOBE_TRACKING_CODE`.
        // KS off — legacy attribution runs as today, including writing
        //          `om_mmc` into `CSF_ATTRIBUTION_DATA` where Adobe reads it.
        // DC's own capture (`captureCsfDynamicCommissioningU5FromProfile` +
        // journey init) is wired separately and unaffected by this gate.
        if (isDynamicCommissioningEnabled()) {
            captureCSFAdobeTrackingCode({ creatorProfile });

            return;
        }

        captureAttributionData({
            section,
            identifier,
            creatorProfile
        });
    };

    renderRouteFromPath(path) {
        const { handle, section, identifier } = getCsfRoute(path);
        const pageType = getPageTypeFromPath(path) || CSF_PAGE_TYPES.FEATURED;

        if (this.isDynamicRoute(section, identifier)) {
            this.renderDynamicRoute(section, identifier, handle, pageType);
        } else if (this.isStaticRoute(section)) {
            this.renderStaticRoute(section, handle, pageType);
        } else {
            this.renderDefaultRoute(handle);
        }
    }

    isDynamicRoute = (section, identifier) => {
        return identifier && Object.keys(csfDynamicRoutes).some(key => key.startsWith(`${section}/:`));
    };

    isStaticRoute = section => Object.prototype.hasOwnProperty.call(csfRouteComponents, section);

    renderDynamicRoute = (section, identifier, handle, pageType) => {
        const dynamicRouteKey = Object.keys(csfDynamicRoutes).find(key => key.startsWith(`${section}/:`));
        const Component = csfDynamicRoutes[dynamicRouteKey];
        const dynamicParam = dynamicRouteKey ? dynamicRouteKey.split('/:')[1] : null;

        if (!Component || !dynamicParam) {
            Sephora.logger.error(`No dynamic component found for route: ${section}/${identifier}`);
            this.renderDefaultRoute(handle);

            return;
        }

        const componentProps = {
            [dynamicParam]: identifier,
            handle,
            section,
            pageType
        };

        this.setState({ currentComponent: Component, componentProps });

        return;
    };

    renderStaticRoute = (section, handle, pageType) => {
        const Component = csfRouteComponents[section];
        const componentProps = { handle, section, pageType };
        this.setState({ currentComponent: Component, componentProps });
    };

    renderDefaultRoute = handle => {
        const Component = csfRouteComponents[CSF_PAGE_TYPES.FEATURED];
        this.setState({
            currentComponent: Component,
            componentProps: {
                handle,
                section: CSF_PAGE_TYPES.FEATURED,
                pageType: CSF_PAGE_TYPES.FEATURED
            }
        });
    };

    isMainRoute = () => {
        if (typeof window === 'undefined') {
            return false;
        }

        const { section, identifier } = getCsfRoute(window.location.pathname);
        const mainSections = new Set([CSF_PAGE_TYPES.FEATURED, CSF_PAGE_TYPES.POSTS, CSF_PAGE_TYPES.PRODUCTS, CSF_PAGE_TYPES.COLLECTIONS, '']);

        return mainSections.has(section) && !identifier;
    };

    updateCsfSeoMetadata = displayNameOverride => {
        const displayName = displayNameOverride ?? this.props.creatorProfileData?.creatorProfile?.displayName;
        const name = displayName || getCsfText('defaultDisplayName');

        SpaUtils.updateHeaderTags({
            seoTitle: getCsfText('seoPageTitle', [name]),
            seoMetaDescription: getCsfText('seoMetaDescription', [name])
        });
    };

    handleBreadcrumbClick = (e, link) => {
        if (link?.href) {
            e.preventDefault();
            navigateTo(link.href, this.props.dispatch, true);
        }
    };

    renderContentForRoute = () => {
        const { currentComponent: Component, componentProps, isReady } = this.state;
        const { pageData, isSectionContentError, ...restProps } = this.props;

        if (!isReady || !Component) {
            return null;
        }

        if (isSectionContentError) {
            return <SectionContentError />;
        }

        const isMainRoute = this.isMainRoute();
        const CarouselComponent = Sephora.configurationSettings.recsMigration?.isEnabled ? RecServiceCarousel : ConstructorCarousel;

        return (
            <>
                <CSFBackToTopButton isMainRoute={isMainRoute} />
                <Component
                    {...restProps}
                    {...pageData}
                    {...componentProps}
                />
                <Container>
                    {/*
                        CSF-1891: mark this Bestsellers carousel as DC ATB
                        eligible. Reaches through the generic carousel /
                        ProductList / ProductItem chain as a plain pass-through
                        prop and is consumed by AddToBasketButton (gate + U1
                        sentinel). Only reached when render passes the
                        `isSectionContentError` guard above — error/empty
                        states never mount the carousel, so the source cannot
                        leak onto CSF error pages.
                    */}
                    <CarouselComponent
                        marginTop={[5, null, 6]}
                        marginBottom={[4, null, 6]}
                        titleMarginBottom={[4, null, 5]}
                        podId={CONSTRUCTOR_PODS.CREATOR_STOREFRONT}
                        grouping={GROUPING.CREATOR_STOREFRONT}
                        dynamicCommissioningAtbSource={CSF_DC_ATB_SOURCES.BESTSELLERS_CAROUSEL}
                    />
                </Container>
            </>
        );
    };

    render() {
        const {
            dispatch,
            textResources,
            formattedMenuItems,
            pageData,
            creatorStoreFrontData,
            creatorProfileData,
            isCreatorContentEmpty,
            isSectionContentError
        } = this.props;
        const { handle, section, isError } = this.state;

        if (isObjectEmpty(creatorStoreFrontData) && isObjectEmpty(creatorProfileData)) {
            return null;
        }

        if (creatorProfileData?.error) {
            return <ProfileError />;
        }

        if (isError || (creatorProfileData && isCreatorContentEmpty)) {
            return <CSFContentEmpty pathname={section} />;
        }

        const isMainRoute = this.isMainRoute();
        const breadcrumbs = getBreadcrumbs({ pageData, creatorProfileData, localization: textResources });

        return (
            <div
                css={{ overflowX: 'hidden', width: '100%' }}
                id='csf_page_wrapper'
            >
                <div>
                    {isMainRoute && (
                        <>
                            {creatorProfileData && (
                                <ProfileDisplay
                                    pathName={section}
                                    displayCommissionableLinks={!isSectionContentError}
                                />
                            )}
                            <SimpleChicletNav
                                dispatch={dispatch}
                                handle={handle}
                                menuItems={formattedMenuItems}
                            />
                        </>
                    )}
                </div>
                <div>
                    {!isMainRoute && breadcrumbs?.length > 0 && creatorProfileData && (
                        <>
                            <Container>
                                <Breadcrumb
                                    breadcrumbs={breadcrumbs}
                                    fontSize='sm'
                                    onLinkClick={this.handleBreadcrumbClick}
                                />
                                <ProfileDisplayMiniHeader
                                    dispatch={dispatch}
                                    handle={handle}
                                    displayCommissionableLinks={!isSectionContentError}
                                />
                            </Container>
                        </>
                    )}

                    {this.renderContentForRoute()}
                </div>
            </div>
        );
    }
}

CreatorStoreFront.propTypes = {
    textResources: PropTypes.object,
    formattedMenuItems: PropTypes.array,
    pageData: PropTypes.object,
    creatorProfileData: PropTypes.object,
    creatorStoreFrontData: PropTypes.object,
    initializeCSFPageData: PropTypes.func,
    initializeCSFCreatorProfileData: PropTypes.func,
    dispatch: PropTypes.func
};

export default wrapComponent(CreatorStoreFront, 'CreatorStoreFront', true);
